import { v4 as uuidv4 } from 'uuid';
import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import Global from '../../../../Services/Common/Global';

const LEG_BASIC_EXCEL_TEMPLATE_REL_PATH =
  'files/ExcellExportFrom/LegalCompliance/안전보건관계법령이행점검표.xlsx';
const LEG_BASIC_EXCEL_HEADER_SCAN_MAX_ROW = 80;

/** 양식: 이행여부 D~F 병합, 비고 G~J 병합 (마스터 셀 D, G) */
const LEG_TEMPLATE_COMPLIANCE_COL_FROM = 4;
const LEG_TEMPLATE_COMPLIANCE_COL_TO = 6;
const LEG_TEMPLATE_REMARK_COL_FROM = 7;
const LEG_TEMPLATE_REMARK_COL_TO = 10;

/** 1행 헤더 → 필드별 열 번호(1-based) */
const HEADER_DEFS = [
  {
    field: 'evalId',
    keys: ['평가id', 'eval_id', 'evalid', 'eval_id'],
  },
  {
    field: 'lwNm',
    keys: ['법령명', '법규명', 'lw_nm', 'lwnm'],
  },
  {
    field: 'checkItem',
    keys: ['점검항목', '점검 항목', 'check_item', 'checkitem'],
  },
  {
    field: 'checkContent',
    keys: ['점검내용', '점검 내용', 'check_content', 'checkcontent'],
  },
  {
    field: 'complianceStatus',
    keys: ['이행여부', '이행 여부', 'compliance_status', 'compliancestatus'],
  },
  {
    field: 'remark',
    keys: ['비고', 'remark'],
  },
];

const NormalizeHeaderToken = (s) =>
  String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');

/** 셀 헤더 토큰이 정의 키와 일치하는지 (템플릿: 「이행 여부 (이행, 미이행, 해당 없음)」 등 괄호 설명 접두 매칭) */
const HeaderKeyMatchesCellToken = (def, token) => {
  if (!token) return false;
  return def.keys.some((k) => {
    const nk = NormalizeHeaderToken(k);
    if (!nk) return false;
    if (token === nk) return true;
    if (def.field === 'complianceStatus' && token.startsWith(nk)) return true;
    return false;
  });
};

export const LegBasicReadExcelCell = (cell) => {
  if (!cell) return '';
  let value = cell.value;
  if (value == null || value === undefined) return '';
  if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value.richText) && value.richText.length > 0) {
      return value.richText.map((item) => item?.text || '').join('').trim();
    }
    if (value.result != null) return String(value.result).trim();
    if (value.text != null) return String(value.text).trim();
    return String(value).trim();
  }
  return String(value).trim();
};

/** 시트에서「법령명」(구「법규명」) 열이 있는 데이터 헤더 행 탐색 (템플릿 상단 타이틀 대응) */
export const LegBasicFindDataHeaderRowOnSheet = (worksheet) => {
  if (!worksheet) return null;
  for (let r = 1; r <= LEG_BASIC_EXCEL_HEADER_SCAN_MAX_ROW; r += 1) {
    const row = worksheet.getRow(r);
    const colByField = LegBasicParseExcelHeaderRow(row);
    if (colByField.lwNm) {
      return { headerRowNumber: r, colByField };
    }
  }
  return null;
};

export const LegBasicParseExcelHeaderRow = (headerRow) => {
  const colByField = {};
  if (!headerRow) return colByField;
  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const token = NormalizeHeaderToken(LegBasicReadExcelCell(cell));
    for (const def of HEADER_DEFS) {
      if (HeaderKeyMatchesCellToken(def, token)) {
        colByField[def.field] = colNumber;
        break;
      }
    }
  });
  return colByField;
};

export const LegBasicNormalizeComplianceExcel = (v) => {
  const s = String(v ?? '').trim();
  if (!s) return '';
  if (['이행', 'y', 'yes', '예'].includes(s.toLowerCase())) return '이행';
  if (['미이행', 'n', 'no', '아니오'].includes(s.toLowerCase())) return '미이행';
  if (['해당없음', '해당 없음', '0', '해당무'].includes(s)) return '해당없음';
  return s;
};

const NormalizeEvalId = (v) => {
  if (v == null || v === '') return '';
  return String(v).trim();
};

/** 그리드 행의 저장 비교용 스냅샷(구 API 필드·이행 Y/N 병기) */
export const LegBasicSnapshotFromGrid = (r) => {
  if (!r) {
    return {
      lwNm: '',
      checkItem: '',
      checkContent: '',
      complianceStatus: '',
      remark: '',
    };
  }
  const rawComp =
    r.complianceStatus ??
    r.COMPLIANCE_STATUS ??
    r.lwCompliance ??
    r.LW_COMPLIANCE ??
    '';
  const s = String(rawComp ?? '').trim();
  let complianceStatus = '';
  if (s === 'Y') complianceStatus = '이행';
  else if (s === 'N') complianceStatus = '미이행';
  else if (s === '0' || rawComp === 0) complianceStatus = '해당없음';
  else complianceStatus = LegBasicNormalizeComplianceExcel(rawComp);

  return {
    lwNm: String(r.lwNm ?? r.LW_NM ?? '').trim(),
    checkItem: String(
      r.checkItem ??
        r.CHECK_ITEM ??
        r.lwProvisions ??
        r.LW_PROVISIONS ??
        ''
    ).trim(),
    checkContent: String(
      r.checkContent ?? r.CHECK_CONTENT ?? r.lwMain ?? r.LW_MAIN ?? ''
    ).trim(),
    complianceStatus,
    remark: String(
      r.remark ?? r.REMARK ?? r.lwRemark ?? r.LW_REMARK ?? ''
    ).trim(),
  };
};

const PickExcelOrKeepBaseStr = (excelVal, baseEff) => {
  const ex = excelVal == null ? '' : String(excelVal).trim();
  if (ex === '') return baseEff;
  return ex;
};

const PickExcelOrKeepBaseComp = (excelVal, baseEff) => {
  const ex = excelVal == null ? '' : String(excelVal).trim();
  if (ex === '') return baseEff;
  return LegBasicNormalizeComplianceExcel(ex);
};

/** 엑셀 행 + 기존 행 기준 실제 반영될 스냅샷(빈 셀은 기존값 유지) */
const LegBuiltMergeSnapshotFromExcel = (p, base) => {
  const b = LegBasicSnapshotFromGrid(base);
  return {
    lwNm: PickExcelOrKeepBaseStr(p.lwNm, b.lwNm),
    checkItem: PickExcelOrKeepBaseStr(p.checkItem, b.checkItem),
    checkContent: PickExcelOrKeepBaseStr(p.checkContent, b.checkContent),
    complianceStatus: PickExcelOrKeepBaseComp(
      p.complianceStatus,
      b.complianceStatus
    ),
    remark: PickExcelOrKeepBaseStr(p.remark, b.remark),
  };
};

const SnapshotsEqual = (a, b) =>
  a.lwNm === b.lwNm &&
  a.checkItem === b.checkItem &&
  a.checkContent === b.checkContent &&
  a.complianceStatus === b.complianceStatus &&
  a.remark === b.remark;

/**
 * 엑셀 행에 대응하는 기존 그리드 행 찾기 (우선순위)
 * 1) 평가ID  2) 법령명+점검항목  3) 점검항목+점검내용(법령명만 수정한 경우)
 * 4) 점검항목이 그리드 전체에서 유일할 때만
 */
const LegBasicResolveExcelRowToBase = (
  p,
  { byEvalId, byLwNmCheckItem, byCheckItemCheckContent, byCheckItemSingleton }
) => {
  const eid = NormalizeEvalId(p.evalId);
  if (eid && byEvalId.has(eid)) return byEvalId.get(eid);

  const lw = String(p.lwNm ?? '').trim();
  const ci = String(p.checkItem ?? '').trim();
  const cc = String(p.checkContent ?? '').trim();

  if (lw && ci) {
    const k = `${lw}\t${ci}`;
    if (byLwNmCheckItem.has(k)) return byLwNmCheckItem.get(k);
  }
  if (ci && cc) {
    const k2 = `${ci}\t${cc}`;
    if (byCheckItemCheckContent.has(k2)) return byCheckItemCheckContent.get(k2);
  }
  if (ci && byCheckItemSingleton.has(ci)) return byCheckItemSingleton.get(ci);

  return null;
};

/**
 * 엑셀에서 읽은 행 배열을 모달 그리드 데이터와 병합
 * - 평가ID → 법령명+점검항목 → 점검항목+점검내용 → 점검항목(유일) 순으로 기존 행 매칭
 * - 신규만 newYn
 * - 엑셀에 없는 기존 행은 뒤에 유지
 */
export const LegBasicMergeExcelIntoModalRows = (modalRows, parsedRows) => {
  const source = (modalRows || []).filter((r) => r && r.deleteYn !== 'Y');
  const byEvalId = new Map();
  const byLwNmCheckItem = new Map();
  const byCheckItemCheckContent = new Map();
  const checkItemCounts = new Map();

  source.forEach((r) => {
    const id = r.evalId ?? r.EVAL_ID;
    const sid = NormalizeEvalId(id);
    if (sid) byEvalId.set(sid, r);

    const s = LegBasicSnapshotFromGrid(r);
    if (s.lwNm && s.checkItem) {
      const k = `${s.lwNm}\t${s.checkItem}`;
      if (!byLwNmCheckItem.has(k)) byLwNmCheckItem.set(k, r);
    }
    if (s.checkItem && s.checkContent) {
      const k2 = `${s.checkItem}\t${s.checkContent}`;
      if (!byCheckItemCheckContent.has(k2)) byCheckItemCheckContent.set(k2, r);
    }
    if (s.checkItem) {
      checkItemCounts.set(
        s.checkItem,
        (checkItemCounts.get(s.checkItem) || 0) + 1
      );
    }
  });

  const byCheckItemSingleton = new Map();
  source.forEach((r) => {
    const s = LegBasicSnapshotFromGrid(r);
    if (
      s.checkItem &&
      checkItemCounts.get(s.checkItem) === 1 &&
      !byCheckItemSingleton.has(s.checkItem)
    ) {
      byCheckItemSingleton.set(s.checkItem, r);
    }
  });

  const lookup = {
    byEvalId,
    byLwNmCheckItem,
    byCheckItemCheckContent,
    byCheckItemSingleton,
  };

  const merged = [];
  const consumedBaseNos = new Set();

  for (const p of parsedRows) {
    const base = LegBasicResolveExcelRowToBase(p, lookup);

    if (base && !consumedBaseNos.has(base.no)) {
      consumedBaseNos.add(base.no);
      const baseSnap = LegBasicSnapshotFromGrid(base);
      const mergedSnap = LegBuiltMergeSnapshotFromExcel(p, base);

      if (SnapshotsEqual(mergedSnap, baseSnap)) {
        const clean = { ...base };
        delete clean.editYn;
        merged.push(clean);
      } else {
        merged.push({
          ...base,
          lwNm: mergedSnap.lwNm,
          checkItem: mergedSnap.checkItem,
          checkContent: mergedSnap.checkContent,
          complianceStatus: mergedSnap.complianceStatus,
          remark: mergedSnap.remark,
          editYn: 'Y',
        });
      }
    } else {
      merged.push({
        no: uuidv4(),
        newYn: 'Y',
        lwNm: String(p.lwNm ?? '').trim(),
        checkItem: String(p.checkItem ?? '').trim(),
        checkContent: String(p.checkContent ?? '').trim(),
        complianceStatus: LegBasicNormalizeComplianceExcel(
          p.complianceStatus ?? ''
        ),
        remark: String(p.remark ?? '').trim(),
      });
    }
  }

  for (const r of source) {
    if (consumedBaseNos.has(r.no)) continue;
    merged.push({ ...r });
  }

  return merged;
};

/** 엑셀 시트에서 데이터 행 파싱 (헤더 행 다음부터, 헤더는 상단 스캔) */
export const LegBasicParseExcelSheetToRows = (worksheet) => {
  const found = LegBasicFindDataHeaderRowOnSheet(worksheet);
  if (!found || !found.colByField.lwNm) {
    return { error: 'HEADER_LW_NM' };
  }
  const { headerRowNumber, colByField } = found;

  const parsed = [];
  const maxRow = 5000;
  for (let rowNum = headerRowNumber + 1; rowNum <= maxRow; rowNum += 1) {
    const row = worksheet.getRow(rowNum);
    const lwNm = colByField.lwNm
      ? LegBasicReadExcelCell(row.getCell(colByField.lwNm))
      : '';
    const checkItem = colByField.checkItem
      ? LegBasicReadExcelCell(row.getCell(colByField.checkItem))
      : '';
    const checkContent = colByField.checkContent
      ? LegBasicReadExcelCell(row.getCell(colByField.checkContent))
      : '';
    const remark = colByField.remark
      ? LegBasicReadExcelCell(row.getCell(colByField.remark))
      : '';

    if (
      !String(lwNm).trim() &&
      !String(checkItem).trim() &&
      !String(checkContent).trim() &&
      !String(remark).trim()
    ) {
      continue;
    }

    if (!String(lwNm).trim()) {
      continue;
    }

    const evalIdRaw = colByField.evalId
      ? LegBasicReadExcelCell(row.getCell(colByField.evalId))
      : '';
    const compRaw = colByField.complianceStatus
      ? LegBasicReadExcelCell(row.getCell(colByField.complianceStatus))
      : '';

    parsed.push({
      evalId: evalIdRaw,
      lwNm: String(lwNm).trim(),
      checkItem: String(checkItem).trim(),
      checkContent: String(checkContent).trim(),
      complianceStatus: LegBasicNormalizeComplianceExcel(compRaw),
      remark: String(remark).trim(),
    });
  }

  return { rows: parsed };
};

const ColNumberToLetters = (n) => {
  let num = n;
  let s = '';
  while (num > 0) {
    const mod = (num - 1) % 26;
    s = String.fromCharCode(65 + mod) + s;
    num = Math.floor((num - 1) / 26);
  }
  return s;
};

const LegBasicSafeMergeRowRange = (worksheet, rowNum, colFrom, colTo) => {
  if (!worksheet || colFrom >= colTo) return;
  const range = `${ColNumberToLetters(colFrom)}${rowNum}:${ColNumberToLetters(
    colTo
  )}${rowNum}`;
  try {
    worksheet.mergeCells(range);
  } catch (_) {
    /* 동일 병합 등 */
  }
};

/** 템플릿 서체·테두리 유지 + 줄바꿈(개행) */
const ApplyDataCell = (row, colNumber, rawValue, horizontal) => {
  if (!row || !colNumber) return;
  const cell = row.getCell(colNumber);
  const str = rawValue != null && rawValue !== '' ? String(rawValue) : '';
  const { font, border, fill } = cell;
  cell.numFmt = '@';
  cell.value = str;
  cell.alignment = {
    vertical: 'middle',
    wrapText: true,
    horizontal: horizontal || 'left',
  };
  cell.style = {
    font,
    alignment: cell.alignment,
    border,
    fill,
    numFmt: '@',
  };
};

const EstimateWrappedLines = (text, charsPerLine) => {
  const s = String(text ?? '');
  const byBreak = s.split(/\r?\n/).reduce((acc, line) => {
    const len = line.length;
    return acc + Math.max(1, Math.ceil(len / charsPerLine));
  }, 0);
  return Math.max(1, byBreak);
};

const ApplyLegBasicDataRowLayout = (row, snap) => {
  const linesA = EstimateWrappedLines(snap.lwNm, 14);
  const linesB = EstimateWrappedLines(snap.checkItem, 18);
  const linesC = EstimateWrappedLines(snap.checkContent, 42);
  const linesD = EstimateWrappedLines(snap.complianceStatus, 10);
  const linesG = EstimateWrappedLines(snap.remark, 24);
  const lines = Math.max(linesA, linesB, linesC, linesD, linesG);
  const h = Math.min(409, Math.max(28, 14 + lines * 15));
  row.height = h;
};

/**
 * 서버 템플릿(안전보건관계법령이행점검표.xlsx)을 불러와 양식 유지 후 그리드 데이터만 기입
 */
export const LegBasicDownloadExcelFromRows = async (modalRows) => {
  const templateUrl = `${Global.file.useUrl}${LEG_BASIC_EXCEL_TEMPLATE_REL_PATH}`;
  const response = await fetch(templateUrl);
  if (!response.ok) {
    throw new Error(
      `템플릿을 불러올 수 없습니다. (${response.status}) 경로를 확인해 주세요.`
    );
  }
  const arrayBuffer = await response.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  try {
    workbook.media = [];
    workbook.eachSheet((ws) => {
      if (ws && Array.isArray(ws._media)) {
        ws._media = [];
      }
    });
  } catch (e) {
    console.warn('[LEG_BASIC] 엑셀 미디어 초기화:', e);
  }

  const worksheet = workbook.worksheets[0];
  if (!worksheet) {
    throw new Error('템플릿 시트를 불러오지 못했습니다.');
  }

  const headerInfo = LegBasicFindDataHeaderRowOnSheet(worksheet);
  if (!headerInfo) {
    throw new Error(
      '템플릿에서 데이터 헤더(「법령명」열)를 찾을 수 없습니다. 서버 파일을 확인해 주세요.'
    );
  }

  const { headerRowNumber, colByField } = headerInfo;
  const dataStartRow = headerRowNumber + 1;
  const rows = (modalRows || []).filter((r) => r && r.deleteYn !== 'Y');

  const colLw = colByField.lwNm ?? 1;
  const colCi = colByField.checkItem ?? 2;
  const colCc = colByField.checkContent ?? 3;
  const colComp = LEG_TEMPLATE_COMPLIANCE_COL_FROM;
  const colRm = LEG_TEMPLATE_REMARK_COL_FROM;

  const colCWidth = worksheet.getColumn(colCc).width;
  if (colCWidth == null || Number(colCWidth) < 36) {
    worksheet.getColumn(colLw).width = 18;
    worksheet.getColumn(colCi).width = 28;
    worksheet.getColumn(colCc).width = 54;
  }

  rows.forEach((item, index) => {
    const rowNum = dataStartRow + index;
    const row = worksheet.getRow(rowNum);

    LegBasicSafeMergeRowRange(
      worksheet,
      rowNum,
      LEG_TEMPLATE_COMPLIANCE_COL_FROM,
      LEG_TEMPLATE_COMPLIANCE_COL_TO
    );
    LegBasicSafeMergeRowRange(
      worksheet,
      rowNum,
      LEG_TEMPLATE_REMARK_COL_FROM,
      LEG_TEMPLATE_REMARK_COL_TO
    );

    const snap = LegBasicSnapshotFromGrid(item);
    const evalId = item.evalId ?? item.EVAL_ID ?? '';
    if (colByField.evalId) {
      ApplyDataCell(
        row,
        colByField.evalId,
        evalId !== '' ? String(evalId) : '',
        'left'
      );
    }
    ApplyDataCell(row, colLw, snap.lwNm, 'left');
    ApplyDataCell(row, colCi, snap.checkItem, 'left');
    ApplyDataCell(row, colCc, snap.checkContent, 'left');
    ApplyDataCell(row, colComp, snap.complianceStatus, 'center');
    ApplyDataCell(row, colRm, snap.remark, 'left');

    ApplyLegBasicDataRowLayout(row, snap);
    row.commit();
  });

  if (colByField.evalId) {
    worksheet.getColumn(colByField.evalId).hidden = true;
  }

  const fileName = `안전보건관계법령이행점검표_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
};
